import { DownloadOutlined, PlusOutlined } from '@ant-design/icons';
import { useQuery } from '@apollo/client';
import { useAuth0 } from '@auth0/auth0-react';
import { Button, message, Modal, Spin, Upload } from 'antd';
import axios from 'axios';
import _ from 'lodash';
import prettyBytes from 'pretty-bytes';
import PropTypes from 'prop-types';
import React, { memo, useEffect, useState } from 'react';

import { SINGLE_FILE } from '@marketreach/services/apollo/file';
import { getFileUploadImage } from '@marketreach/utils/files';

/**
 * Property upload component
 * @param props
 * @return {JSX.Element}
 * @constructor
 */
const PropertiesTemplateUpload = (props) => {
  const {
    imageUrl,
    readOnly,
    onChange: propsOnChange,
    client,
    validation,
  } = props;

  const [fileList, setFileList] = useState([]);
  const { getAccessTokenSilently } = useAuth0();

  const [accessToken, setAccessToken] = useState();

  useEffect(() => {
    if (imageUrl) {
      if (typeof imageUrl === 'string' || imageUrl instanceof String) {
        if (!_.isEqual(fileList, [{ thumbUrl: imageUrl }])) {
          setFileList([{ thumbUrl: imageUrl }]);
        }
      } else {
        if (_.isArray(imageUrl)) {
          if (imageUrl.length > 0 && !_.isEqual(fileList, imageUrl)) {
            setFileList(imageUrl);
          }
        } else {
          if (!_.isEqual(fileList, [imageUrl])) {
            setFileList([imageUrl]);
          }
        }
      }
    }
  }, [imageUrl]);

  useEffect(() => {
    if (propsOnChange) {
      propsOnChange(fileList);
    }
  }, [fileList]);

  useEffect(() => {
    let isMounted = true; // note mutable flag

    getAccessTokenSilently()
      .then((token) => {
        if (isMounted) {
          return setAccessToken(token);
        }
      })
      .catch((err) => {
        console.error('64 err: ', err);
      });
  }, [getAccessTokenSilently]);

  /**
   * Get base64 string from file object
   *
   * @param file
   * @return {Promise<unknown>}
   */
  const getBase64 = (file) =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result);
      reader.onerror = (error) => reject(error);
    });

  const [previewVisible, setPreviewVisible] = useState(false);
  const [previewImage, setPreviewImage] = useState(true);
  const [previewTitle, setPreviewTitle] = useState('');
  const [previewFile, setPreviewFile] = useState();
  /**
   * Handle cancel upload
   */
  const handleCancel = () => setPreviewVisible(false);

  const {
    data: fileDataResponse,
    loading: loadingFileData,
    refetch: refetchFileData,
  } = useQuery(SINGLE_FILE, {
    variables: {
      clientCode: client.apiId,
      id: previewFile?.id,
    },
    skip: !previewFile?.id,
  });

  const fileData = fileDataResponse?.file?.data || false;

  /**
   * Handle preview file:
   * - if that is image - display thumb
   * - for another file types - display icon for known types
   * - for all other cases - display default icon
   *
   * @param previewFileInfo
   * @return {Promise<void>}
   */
  const handlePreview = async (previewFileInfo) => {
    const file = previewFileInfo;
    setPreviewFile(file);
    if (!file.url && !file.preview) {
      file.preview = await getBase64(file.originFileObj);
    }
    setPreviewImage(file.url || file.preview);
    setPreviewVisible(true);
    setPreviewTitle(
      file.name || file.url.substring(file.url.lastIndexOf('/') + 1)
    );
    await refetchFileData({
      variables: {
        clientCode: client.apiId,
        id: previewFileInfo?.id || ' ',
      },
    });
  };

  /**
   * Check count of uploaded files
   *
   * @param showAlert
   * @return {boolean}
   */
  const checkFilesCountLimit = (showAlert = false) => {
    if (validation?.file_count_limit) {
      if (validation?.file_count_limit?.max) {
        if (fileList.length > validation?.file_count_limit?.max) {
          if (showAlert) {
            message.error(
              validation?.file_count_limit?.error ||
                `Max files count should be ${validation?.file_count_limit?.max}, now ${fileList.length}`
            );
          }
          return false;
        }
      }
      if (validation?.file_count_limit?.min) {
        if (fileList.length <= validation?.file_count_limit?.min) {
          if (showAlert) {
            message.error(
              validation?.file_count_limit?.error ||
                `Min files count should be ${validation?.file_count_limit?.min}, now ${fileList.length}`
            );
          }
          return false;
        }
      }
    }
    return true;
  };

  /**
   * Check file size limit
   * To prevent upload file that not matched limit in property settigns
   *
   * @param file
   * @param showAlert
   * @return {boolean}
   */
  const checkFilesSizeLimit = (file, showAlert = false) => {
    if (validation?.file_size_limit) {
      if (validation?.file_size_limit?.max) {
        if (file.size > validation?.file_size_limit?.max) {
          if (showAlert) {
            message.error(
              validation?.file_size_limit?.error ||
                `Max file size should be ${prettyBytes(
                  validation?.file_size_limit?.max
                )}, now ${prettyBytes(file.size)}`
            );
          }
          return false;
        }
      }
      if (validation?.file_size_limit?.min) {
        if (file.size < validation?.file_size_limit?.min) {
          if (showAlert) {
            message.error(
              validation?.file_size_limit?.error ||
                `Min file size should be ${prettyBytes(
                  validation?.file_size_limit?.min
                )}, now ${prettyBytes(file.size)}`
            );
          }
          return false;
        }
      }
    }
    return true;
  };

  /**
   * Handler for upload component changes
   * Tracked state:
   * - upload finish - prepare file object
   * - check restriction
   *
   * @param file
   * @param fileListArr
   * @return {Promise<void>}
   */
  const handleChange = async ({ file, fileList: fileListArr }) => {
    if (file.status !== 'uploading' && fileListArr) {
      const fileItems = await fileListArr
        .map((item) => {
          if (item.response) {
            const compiledItem = {
              id: item.response._id,
              name: item.response.name,
              status: 'done',
              url: item.response.url,
              thumbUrl: item.response.url,
              type: item.response.mimeType,
            };

            propsOnChange(compiledItem);
            return compiledItem;
          }
          return item;
        })
        .filter((item) => Object.keys(item).length > 0)
        // filter files that not matched files sizes limits
        .filter((item) => checkFilesSizeLimit(item, false));

      // Remove file duplication
      return setFileList(_.uniqBy(fileItems, (item) => item.id));
    }
    // Remove file duplication
    return setFileList(_.uniqBy(fileListArr, (item) => item.id));
  };

  const handleRemove = async (file) => {
    // Prevent remove by files count limit rule
    if (!checkFilesCountLimit(true)) {
      return false;
    }
    propsOnChange(true);
    await axios.delete(`${process.env.REACT_APP_BASE_URI}/files`, {
      data: {
        id: file.id,
        clientCode: client?.apiId,
      },
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
    });

    setFileList(fileList.filter((item) => item.id !== file.id));
  };

  const beforeUpload = (file) => {
    // check sizes and counts limitation
    const checkResult =
      checkFilesSizeLimit(file, true) && checkFilesCountLimit(true);
    return checkResult;
  };

  const uploadButton = (
    <div>
      <PlusOutlined />
      <div className="ant-upload-text">Upload</div>
    </div>
  );

  /**
   * Prepare file object
   * Add thumbUrl
   *
   * @param file
   * @return {*&{thumbUrl: string}}
   */
  const prepareFileUrls = (file) => {
    return {
      ...file,
      thumbUrl: getFileUploadImage(file),
    };
  };

  return (
    <div className="properties-template-upload">
      <Upload
        className={readOnly ? 'properties-template-upload-readonly' : ''}
        action={`${process.env.REACT_APP_BASE_URI}/files/upload`}
        listType="picture-card"
        fileList={fileList.map(prepareFileUrls)}
        onPreview={handlePreview}
        onChange={handleChange}
        onRemove={handleRemove}
        beforeUpload={beforeUpload}
        maxCount={validation?.file_count_limit?.max || 0}
        readOnly={readOnly}
        headers={{
          Authorization: `Bearer ${accessToken}`,
        }}
        data={{
          clientCode: client?.apiId,
        }}
      >
        {readOnly ? null : uploadButton}
      </Upload>
      <Modal
        visible={previewVisible}
        title={previewTitle}
        footer={null}
        onCancel={handleCancel}
        forceRender
        getContainer={false}
      >
        <Spin spinning={loadingFileData}>
          {fileData?.createdAt && (
            <p>
              <b>Upload date: </b>
              {fileData.createdAt}
            </p>
          )}
          {fileData?.type && (
            <p>
              <b>Mime type: </b>
              {fileData.type}
            </p>
          )}
          {fileData?.info?.size && (
            <p>
              <b>File size: </b>
              {prettyBytes(fileData?.info?.size)}
            </p>
          )}
        </Spin>
        {previewFile?.type && previewFile?.type.indexOf('image') !== -1 && (
          <img
            alt={previewTitle}
            style={{ width: '100%' }}
            src={previewImage}
          />
        )}
        {previewFile?.url && (
          <p>
            <Button
              type="primary"
              icon={<DownloadOutlined />}
              href={previewFile.url}
              target="_blank"
            >
              Download
            </Button>
          </p>
        )}
      </Modal>
    </div>
  );
};

PropertiesTemplateUpload.propTypes = {
  imageUrl: PropTypes.oneOfType([
    PropTypes.instanceOf(Object),
    PropTypes.string,
  ]),
  readOnly: PropTypes.bool,
  onChange: PropTypes.func,
  validation: PropTypes.instanceOf(Object),
};

PropertiesTemplateUpload.defaultProps = {
  imageUrl: {},
  readOnly: false,
  onChange: () => {},
  validation: {},
};

export default memo(PropertiesTemplateUpload);
